import "./SubscriptionPlans.css";
import "./SubscriptionPlansNewTable.css";

import { COUNTRIES_VAT, planFeatures } from "./contants";
import React, { useMemo, useRef, useState } from "react";

import { Elements } from "@stripe/react-stripe-js";
import PaymentView from "./PaymentView/PaymentView";
import { createPaymentForPlan } from "../../services/subsriptionService";
import { loadStripe } from "@stripe/stripe-js";
import { selectUser } from "../../redux/reducers/userReducer";
import { useFetchAllSubscriptionPlan } from "../../hooks/APIs/useFetchAllSubscriptionPlan";
import { useLoader } from "../../hooks/useLoader";
import { useSelector } from "react-redux";

const stripePromise = loadStripe(
  "pk_live_51H5SBGAdr6nVMtS6NnshRyIHwyRdM7O7mxgmIhQiUK0vTuJRGS3JVHkV8eIZNJqI49AOdlus2lDJGHEOBxbmicAc00GtiMyZH8",
);

// Define the plan type interface
interface Plan {
  id: string;
  name: string; // tier: Basic / Standard / Pro
  type: string;
  subscriptionType?: string; // billing period: monthly / annually
  price: number;
  askForPrice?: boolean;
}

// Define the plans object type
interface PlansForShow {
  [key: string]: {
    [key: string]: Plan;
  };
}

function SubscriptionPlansNewTable() {
  const [clientSecret, setClientSecret] = useState("");
  const [vatAmount, setVatAmount] = useState(0);
  const [vatRate, setVatRate] = useState(0);

  const selectedPlan = useRef<Plan | null>(null);
  const { showLoading } = useLoader();
  const user = useSelector(selectUser);

  const { allPlans } = useFetchAllSubscriptionPlan();

  // Function to detect country from browser
  const detectCountryFromBrowser = async (): Promise<string> => {
    try {
      // INFO: alt endpoint // https://get.geojs.io/v1/ip/geo.json
      // https://api.ipwho.org/me?apiKey=sk.81dd9f0a5d8d00673bd18f25f3b8a53eeebb0cd8735cde1ee423ab4fc6d71a76&get=geoLocation
      const response = await fetch("https://get.geojs.io/v1/ip/geo.json");
      const data = await response.json();
      const ipCountry = data.country_code?.toUpperCase();
      if (
        ipCountry &&
        COUNTRIES_VAT.find((country) => country.code === ipCountry)
      ) {
        return ipCountry;
      }
    } catch (geoError) {
      // IP geolocation failed, continue to default
    }
    return "0";
  };

  const plansForShow = useMemo((): PlansForShow => {
    if (allPlans.length === 0) {
      return {};
    }

    const allFilteredPlans = allPlans?.reduce(
      (acc: PlansForShow, plan: Plan) => {
        const key = plan.name?.toLowerCase();
        // The billing period may live in either field depending on the doc
        // schema (current catalog docs: type = monthly/annually,
        // subscriptionType = tier) — pick whichever holds a valid value.
        const billingPeriod =
          plan.subscriptionType === "monthly" ||
          plan.subscriptionType === "annually"
            ? plan.subscriptionType
            : plan.type;
        if (acc[key]) {
          acc[key][billingPeriod] = plan;
        } else {
          acc[key] = { [billingPeriod]: plan };
        }
        return acc;
      },
      {},
    );

    return allFilteredPlans;
  }, [allPlans]);

  const handlePlanBtnClick = async (plan: Plan) => {
    if (plan.askForPrice) {
      window.location.href = "mailto:subscriptions@eurointelligence.com";
      return;
    }
    selectedPlan.current = plan;

    // Automatically detect country and proceed with payment
    const detectedCountry = await detectCountryFromBrowser();
    console.log("detectedCountry", detectedCountry);

    await fetchPaymentInfoForPlan(plan, detectedCountry);
  };

  const fetchPaymentInfoForPlan = async (plan: Plan, countryCode: string) => {
    if (!user?.data?.user?.user_id) {
      alert("Please login or signup first!");
      return;
    }
    try {
      showLoading(true);
      const res = await createPaymentForPlan({
        email: user?.data?.user?.user_id || "",
        planId: plan.id || "",
        country: countryCode,
      });
      setClientSecret(res.data?.data?.clientSecret ?? "");
      setVatAmount(res.data?.data?.vatAmount ?? 0);
      setVatRate(res.data?.data?.vatRate ?? 0);
      showLoading(false);
    } catch (error) {
      showLoading(false);
    }
  };

  const handleCloseForPaymentView = () => {
    selectedPlan.current = null;
    setClientSecret("");
  };

  const renderPaymentView = () => {
    return (
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret: clientSecret,
        }}
      >
        <PaymentView
          selectedPlan={selectedPlan.current}
          selectedCountry={detectCountryFromBrowser()}
          vatAmount={vatAmount}
          vatRate={vatRate * 100}
          onClosePress={handleCloseForPaymentView}
        />
      </Elements>
    );
  };

  const renderAllPlans = () => {
    return (
      <div>
        <div className="heading-subheading-conatiner">
          <div className="Plans-Price-heading">Plans & Pricing</div>
        </div>
        <div>
          <table className="table-plan">
            <thead>
              <tr>
                <th className="table-plan-td-bold"></th>
                <th className="table-plan-td-bold">Basic</th>
                <th className="table-plan-td-bold">Standard</th>
                <th className="table-plan-td-bold">Pro</th>
                <th className="table-plan-td-bold">Group</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="table-plan-td"></td>
                <td className="table-plan-td">Single</td>
                <td className="table-plan-td">Single</td>
                <td className="table-plan-td">Single</td>
                <td className="table-plan-td">Companies/Institutions</td>
              </tr>
              <tr>
                <td className="table-plan-td-bold"></td>
                <td className="table-plan-td-bold">Select*</td>
                <td className="table-plan-td-bold">Select*</td>
                <td className="table-plan-td-bold">Select*</td>
                <td className="table-plan-td-bold">Enquire*</td>
              </tr>
              <tr>
                <td className="table-plan-td-bold"></td>
                <td className="table-plan-td-bold">
                  <span>
                    <span
                      className="plan-click-span"
                      onClick={() => {
                        handlePlanBtnClick(plansForShow?.basic?.monthly);
                      }}
                    >
                      €{plansForShow?.basic?.monthly?.price ?? ""}/month
                    </span>
                    {" or "}
                    <span
                      className="plan-click-span"
                      onClick={() => {
                        handlePlanBtnClick(plansForShow?.basic?.annually);
                      }}
                    >
                      €{plansForShow?.basic?.annually?.price ?? ""}/year
                    </span>
                  </span>
                </td>
                <td className="table-plan-td-bold">
                  <span>
                    <span
                      className="plan-click-span"
                      onClick={() => {
                        handlePlanBtnClick(plansForShow?.standard?.monthly);
                      }}
                    >
                      €{plansForShow?.standard?.monthly?.price ?? ""}/month
                    </span>
                    {" or "}
                    <span
                      className="plan-click-span"
                      onClick={() => {
                        handlePlanBtnClick(plansForShow?.standard?.annually);
                      }}
                    >
                      €{plansForShow?.standard?.annually?.price ?? ""}/year
                    </span>
                  </span>
                </td>
                <td className="table-plan-td-bold">
                  <span>
                    <span
                      className="plan-click-span"
                      onClick={() => {
                        handlePlanBtnClick(plansForShow?.pro?.monthly);
                      }}
                    >
                      €{plansForShow?.pro?.monthly?.price ?? ""}/month
                    </span>
                    {" or "}
                    <span
                      className="plan-click-span"
                      onClick={() => {
                        handlePlanBtnClick(plansForShow?.pro?.annually);
                      }}
                    >
                      €{plansForShow?.pro?.annually?.price ?? ""}/year
                    </span>
                  </span>
                </td>
                <td className="table-plan-td-bold">
                  <span
                    className="plan-click-span"
                    onClick={() => {
                      handlePlanBtnClick({ askForPrice: true } as Plan);
                    }}
                  >
                    bespoke pricing
                  </span>
                </td>
              </tr>
              {planFeatures.map((item, index) => {
                return (
                  <tr key={item.key}>
                    <td className="table-plan-td-bold">{item.title}</td>
                    <td className="table-plan-td-bold">
                      {item.basic ? "✓" : ""}
                    </td>
                    <td className="table-plan-td-bold">
                      {item.standard ? "✓" : ""}
                    </td>
                    <td className="table-plan-td-bold">
                      {item.pro ? "✓" : ""}
                    </td>
                    <td className="table-plan-td-bold">
                      {item.group ? "✓" : ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="academic-discount-note">
            * academics can enquire about a discount code
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="subscription-plans-container">
      {!!clientSecret ? renderPaymentView() : renderAllPlans()}
    </div>
  );
}

export default SubscriptionPlansNewTable;
